/**
 * 浮层作用域。
 *
 * 普通 Web 应用里「弹窗 = 蒙版 + 锁滚动 + 模态」是默认答案；在地面站里这套默认值是危险的：
 * 飞行员打开一个设置面板，蒙版一盖就再也看不到姿态与电压 —— 而这两样东西恰恰是
 * 「还能不能继续操作」的判断依据。
 *
 * 所以这里把「蒙版」从「弹窗」里拆出来，做成**显式选择**的作用域：
 *
 *   `realtime`（默认）  浮在界面上方，但底下的实时数据仍然看得见、点得到。
 *                       焦点仍然被约束（键盘有界），指针完全放行（鼠标/触摸无界）。
 *   `blocking`          只在真正必须中断操作时用（不可逆、会危及飞行的确认）。
 *                       全屏蒙版、锁滚动、`aria-modal`、点击外部不关闭。
 *
 * 关键约束：**默认值必须是 `realtime`**。默认值决定了绝大多数调用点走哪条路，
 * 而「忘了选」这个失误在 `blocking` 上后果危险（遮住仪表），在 `realtime` 上最多是不够正式。
 * 反过来，需要 `blocking` 的场合是少数且都是刻意写下的（"确认返航"），漏选不会发生。
 */

export type OverlayScope = 'realtime' | 'blocking';

/** 遍历用（例如设置页列出可选作用域），顺序即推荐强度 */
export const OVERLAY_SCOPES: readonly OverlayScope[] = ['realtime', 'blocking'];

export const DEFAULT_OVERLAY_SCOPE: OverlayScope = 'realtime';

/**
 * 「该作用域下允许什么」的唯一出处。
 *
 * 规则做成数据而不是散在各组件里的 `if (scope === 'blocking')`：
 * 新增一种作用域时只需在这里加一行，而每个字段的含义都写着"为什么"。
 */
export interface OverlayScopeRules {
  /** 是否渲染全屏蒙版。蒙版本身就是一层会吞掉指针事件的元素，因此这一条同时决定了"底下能不能点" */
  readonly scrim: boolean;
  /** 是否锁定页面滚动。realtime 不锁 —— 飞行员要能滚到被浮层盖住的遥测面板 */
  readonly lockScroll: boolean;
  /** 是否用 `aria-modal` 宣告模态。realtime 必须为 false，否则读屏软件会把浮层外的内容视为不可用 */
  readonly ariaModal: boolean;
  /** 焦点是否约束在浮层内。两种作用域都为 true：键盘必须有界，否则 Tab 会跑到背后的地图控件上 */
  readonly trapFocus: boolean;
  /** Esc 是否请求关闭。两种作用域都为 true —— Esc 永远是最安全的退出方式 */
  readonly dismissOnEscape: boolean;
  /** 点击浮层外部是否请求关闭。blocking **不允许**：不可逆动作必须由人显式选择，而不是"点偏了就当取消" */
  readonly dismissOnOutsidePointer: boolean;
  /** 层级令牌。blocking 永远在 realtime 之上，与打开顺序无关 */
  readonly zIndex: string;
}

/**
 * 层级档位。与作用域**正交**：作用域决定"允许什么"（蒙版/焦点/关闭方式），
 * 档位决定"压在谁上面"。
 *
 * `default` 用所在作用域自己那一档；`confirm` 用 `--z-confirm`（最高）。
 * 只有按钮确认气泡该用 `confirm`，理由见 `tokens.css` 里 `--z-confirm` 的注释。
 *
 * 名字不叫 `OverlayLayer`：那个名字已经被 `overlayStack` 用来表示"堆栈里的一层"，
 * 而这里是"哪一档 z-index"，两者不是一回事。
 */
export type OverlayTier = 'default' | 'confirm';

export const OVERLAY_TIERS: readonly OverlayTier[] = ['default', 'confirm'];

/**
 * 浮层的基座层级令牌。
 *
 * 做成函数而不是在规则表里加一个字段：层级是**令牌**问题而非**规则**问题，
 * 混在一起会让 `OVERLAY_SCOPE_RULES` 同时承担两件事，而它现在能被逐字段断言。
 */
export function overlayZIndexOf(scope: OverlayScope, tier: OverlayTier | undefined): string {
  return tier === 'confirm' ? 'var(--z-confirm)' : OVERLAY_SCOPE_RULES[scope].zIndex;
}

export const OVERLAY_SCOPE_RULES: Readonly<Record<OverlayScope, OverlayScopeRules>> = {
  realtime: {
    scrim: false,
    lockScroll: false,
    ariaModal: false,
    trapFocus: true,
    dismissOnEscape: true,
    dismissOnOutsidePointer: true,
    zIndex: 'var(--z-popover)',
  },
  blocking: {
    scrim: true,
    lockScroll: true,
    ariaModal: true,
    trapFocus: true,
    dismissOnEscape: true,
    dismissOnOutsidePointer: false,
    zIndex: 'var(--z-modal)',
  },
};

/**
 * 把「可省略的作用域」收敛成一个确定值。
 *
 * 单独一个函数是为了让"省略即 realtime"这件事只有一处定义：
 * 组件在渲染期调用它，规则表只被读一次，测试也能直接断言这个默认值。
 */
export function resolveOverlayScope(scope: OverlayScope | undefined): OverlayScope {
  return scope ?? DEFAULT_OVERLAY_SCOPE;
}

export function scopeRulesOf(scope: OverlayScope): OverlayScopeRules {
  return OVERLAY_SCOPE_RULES[scope];
}

export function isBlockingScope(scope: OverlayScope): boolean {
  return OVERLAY_SCOPE_RULES[scope].scrim;
}
